import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import {
  EnrolmentsResource,
  OngoingCourse,
} from "../../../stores/EnrolmentStore/Types";
import { getRequest } from "../../../utils/httpRequest";

const sortByLastAccessed = (courseA: OngoingCourse, courseB: OngoingCourse) => {
  const dateA = Date.parse(courseA.last_accessed_at || "") || 0;
  const dateB = Date.parse(courseB.last_accessed_at || "") || 0;

  return dateB - dateA;
};

const courseUrl = (enrolment: OngoingCourse) =>
  enrolment.embedded_host_url
    ? enrolment.embedded_host_url
    : `/enrolments/${enrolment.id}`;

const courseImage = (enrolment: OngoingCourse, userIsInUk: boolean) => {
  const course = enrolment?.course;

  if (userIsInUk && course?.preview_headshot_alt_image_url) {
    return course?.preview_headshot_alt_image_url;
  }

  return course?.preview_headshot_image_url;
};

const courseImageAlt = (enrolment: OngoingCourse) =>
  `${enrolment?.course?.name} course thumbnail`;

const progressMeta = (percentageCompleted: number) => {
  if (percentageCompleted >= 80) return "You are nearly there";
  if (percentageCompleted >= 40) return "You are making proper progress";
  if (percentageCompleted > 0) return "You have already made a start";

  return "Ready for your first lesson";
};

interface Props {
  hasActiveEnrolments: boolean;
  userIsInUk: boolean;
}

const DoNext: React.FC<Props> = ({ hasActiveEnrolments, userIsInUk }) => {
  const [activeEnrolment, setActiveEnrolment] = useState<OngoingCourse | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!hasActiveEnrolments) {
      setActiveEnrolment(null);
      setLoaded(true);
      return;
    }

    getRequest("/api/profile/ongoing.json")
      .then((response: AxiosResponse<EnrolmentsResource>) => {
        const sortedCourses = [...response.data].sort(sortByLastAccessed);

        setActiveEnrolment(sortedCourses[0] || null);
        setLoaded(true);
      })
      .catch(() => {
        setActiveEnrolment(null);
        setLoaded(true);
      });
  }, [hasActiveEnrolments]);

  return (
    <section
      className="do-next container"
      aria-label="Pick up where you left off"
    >
      {activeEnrolment ? (
        <div className="do-next-inner" id="doResume">
          <div className="do-next-art">
            <img
              src={courseImage(activeEnrolment, userIsInUk)}
              alt={courseImageAlt(activeEnrolment)}
              loading="lazy"
            />
          </div>

          <div className="do-next-body">
            <div className="do-next-eyebrow">Pick up where you left off</div>
            <div className="do-next-title">{activeEnrolment.course.name}</div>
            <div className="do-next-meta">
              {progressMeta(activeEnrolment.percentage_completed)}
            </div>

            <div className="do-next-progress">
              <div className="do-next-bar">
                <div
                  className="do-next-fill"
                  style={{
                    width: `${activeEnrolment.percentage_completed}%`,
                  }}
                />
              </div>
              <div className="do-next-pct">
                {activeEnrolment.percentage_completed}%
              </div>
            </div>
          </div>

          <div className="do-next-cta">
            <a href={courseUrl(activeEnrolment)} className="btn btn-primary">
              Resume course
            </a>
          </div>
        </div>
      ) : !loaded && hasActiveEnrolments ? (
        <div className="do-next-inner" id="doStart" aria-busy="true">
          <div className="do-next-body">
            <div className="do-next-eyebrow">Finding your next step</div>
            <div className="do-next-title">Loading your active courses</div>
          </div>
        </div>
      ) : null}

      <div className="quick-row">
        <a href="/community" className="quick-link">
          <span className="quick-link-ic hub" aria-hidden="true">
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 200 200"
            >
              <path
                fill="#2E2437"
                d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z"
              />
              <path
                fill="#2E2437"
                d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z"
              />
              <path
                fill="#fff"
                d="M70.59,137.36H84.13v-9.25h7.62V116.46H84.13V78.51h-11l-26.3,41.12v8.48H70.59Zm-9-20.13,10.11-16.79v16.79Z"
              />
              <path
                fill="#fff"
                d="M115.8,124.85v-.17l9.16-9.51c7.8-8.13,9.51-13.19,9.51-19.87,0-11-8.91-17.56-19-17.56s-16.53,5.05-20,11.13L105.26,96c2.57-3.77,5.57-5.48,9.08-5.48a6.08,6.08,0,0,1,6.17,6.34c0,4.2-2.66,7.88-7.28,12.77l-17.4,18.76.18,9h39.06V124.85Z"
              />
            </svg>
          </span>

          <span className="quick-link-tx">
            <span className="t">Go to the Community Hub</span>
            <span className="s">
              Discussions, peer reviews and live office hours
            </span>
          </span>

          <span className="arr" aria-hidden="true">
            →
          </span>
        </a>

        {hasActiveEnrolments && (
          <a href="/courses" className="quick-link">
            <span className="quick-link-ic browse">
              <svg aria-hidden="true">
                <use href="#ln-book" />
              </svg>
            </span>

            <span className="quick-link-tx">
              <span className="t">Browse all courses</span>
              <span className="s">Forty Mini MBAs across four skill areas</span>
            </span>

            <span className="arr" aria-hidden="true">
              →
            </span>
          </a>
        )}
      </div>
    </section>
  );
};

export default DoNext;
